export function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function scalar(value: unknown): string | null {
  if (typeof value === "string") {
    return value.trim() ? value : null;
  }
  if (
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value))
  ) {
    return String(value);
  }
  return null;
}

export function dataRows(value: unknown) {
  const rows = object(value).data;
  return (Array.isArray(rows) ? rows : []).flatMap((raw) => {
    const row = object(raw);
    const type = text(row.type);
    const value = scalar(row.value);
    return type && value !== null
      ? [{ type, value, metricType: text(row.metricType) }]
      : [];
  });
}

export function captionDetails(value: unknown) {
  const caption = text(object(value).caption);
  return caption ? { caption } : null;
}

export function abbyDetails(value: unknown) {
  const data = dataRows(value);
  return data.length ? { data } : null;
}

export function prescriptionDetails(value: unknown) {
  const prescription = text(object(value).prescription);
  return prescription ? { prescription } : null;
}

export const consultationFields = [
  "consultDate",
  "selectedService",
  "partnerPlanName",
  "amount",
  "billed",
] as const;

export function consultationDetails(value: unknown) {
  const caption = text(object(value).caption);
  const rows = dataRows(value);
  const data = consultationFields.flatMap((field) =>
    rows.filter((row) => row.type === field)
  );
  return caption || data.length ? { caption, data } : null;
}

export function pathologyDetails(value: unknown) {
  const data = dataRows(value).filter(
    (row) => row.type.toLowerCase() === "ordernumber"
  );
  return data.length ? { data } : null;
}

export function pathologyOrderId(value: unknown) {
  const rows = object(value).data;
  if (!Array.isArray(rows)) {
    return undefined;
  }
  const row = rows
    .map(object)
    .find(
      (item) =>
        text(item.type).toLowerCase() === "pathologyorderid" && text(item.value)
    );
  return row ? text(row.value) : undefined;
}

function namedItems(value: unknown) {
  return (Array.isArray(value) ? value : []).flatMap((raw) => {
    const item = object(raw);
    const name = text(item.name);
    return name ? [{ name, value: scalar(item.value) ?? "" }] : [];
  });
}

export function diagnosisDetails(value: unknown) {
  const details = object(value);
  const procedure = (
    Array.isArray(details.procedure) ? details.procedure : []
  ).flatMap((raw) => {
    const group = object(raw);
    const name = text(group.name);
    return name ? [{ name, items: namedItems(group.items) }] : [];
  });
  const practiceName = text(details.practiceName);
  const treatmentLocation = text(details.treatmentLocation);
  return procedure.length || practiceName || treatmentLocation
    ? { procedure, practiceName, treatmentLocation }
    : null;
}

export function assistanceDetails(value: unknown) {
  const details = object(value);
  const result: Record<string, unknown> = {};
  for (const key of [
    "recommendation",
    "possibleConditions",
    "consultationOutcome",
    "outcomeStatus",
    "paymentMethod",
    "faceScanResults",
    "rating",
    "more",
  ]) {
    const section = object(details[key]);
    const name = text(section.name);
    let items = namedItems(section.items);
    if (key === "more") {
      items = items.filter(
        (item) => item.value && Number.isFinite(Date.parse(item.value))
      );
    }
    const location = text(section.location);
    if (name && (items.length || (key === "more" && location))) {
      result[key] = { name, items, location };
    }
  }
  return Object.keys(result).length ? result : null;
}
